import React, { useState, useRef, useEffect } from "react";
import { Pencil, Check } from "lucide-react";
import { Device } from "@schemas";
import { updateDevice, getAllSensorAgents } from "../services/apiServices";
import { useModal } from "context/ModalContext";
import { SensorAgent } from "@schemas";
import { useProjectContext } from "context/ProjectContext";

const ViewUpdate: React.FC<{ data?: Device }> = ({ data }) => {
  const { dispatch } = useModal();
  const { state } = useProjectContext();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [deviceName, setDeviceName] = useState<string>(data?.device_name || "");
  const [sensorAgentVersion, setSensorAgentVersion] = useState<string>(
    data?.sensor_agent_version || ""
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const [sensorAgents, setSensorAgents] = useState<any[]>([]);

  useEffect(() => {
    fetchSensorAgents();
  }, []);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  // Function to fetch sensor agents
  const fetchSensorAgents = async () => {
    // const fetchedData = [
    //   { id: 1, name: "Sensor Agent 1", version: "1.0" },
    //   { id: 2, name: "Sensor Agent 2", version: "1.1" },
    //   { id: 3, name: "Sensor Agent 3", version: "1.2" },
    //   { id: 4, name: "Sensor Agent 4", version: "1.3" },
    //   { id: 5, name: "Sensor Agent 5", version: "1.4" },
    //   { id: 6, name: "Sensor Agent 6", version: "1.5" },
    // ];
    if (state.currentProjectID) {
      const fetchedData = await getAllSensorAgents(state.currentProjectID);
      if (data) {
        const filteredData = fetchedData.filter((agent: SensorAgent) => {
          if (agent.sensor_agent_version) {
            return agent.sensor_agent_version > data.sensor_agent_version;
          } else {
            return true;
          }
        });
        setSensorAgents(filteredData);
      }
    }
  };

  const handleUpdateDeviceName = async () => {
    if (!data) {
      setIsEditing(false);
      return;
    }
    if (data.device_name === deviceName.trim()) {
      setIsEditing(false);
      return;
    }

    const { device_index, created_at, ...updatedData } = {
      ...data,
      device_name: deviceName,
    };

    try {
      if (state.currentProjectID) {
        const response = await updateDevice(
          data.device_id,
          updatedData,
          state.currentProjectID
        );
        console.log(response);
        alert("Device Name Updated successful!");
      }
    } catch (err) {
      console.error("Error updating device name:", err);
    } finally {
      setIsEditing(false);
    }
  };

  const updateSensorAgentVersion = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const version = e.target.value;
    if (!data) return;
    const { device_index, created_at, ...updatedData } = {
      ...data,
      sensor_agent_version: version,
    };
    try {
      if (state.currentProjectID) {
        const response = await updateDevice(
          data.device_id,
          updatedData,
          state.currentProjectID
        );
        console.log(response);
        setSensorAgentVersion(version);
      }
    } catch (err) {
      console.error("Error updating sensor agent version:", err);
    }
  };

  return (
    <div className="flex flex-col w-full overflow-auto">
      <div className="w-full bg-custom-lightbg shadow-custom rounded-[10px]">
        {/* Device id and name fields*/}
        <div className="flex items-center">
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              MAC ID
            </label>
            <p
              className={`w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none ${
                !data?.device_id && "h-[49px]"
              }`}
            >
              {data?.device_id}
            </p>
          </div>
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label
              htmlFor="deviceName"
              className="font-bold text-sm text-custom-bluenew"
            >
              Device Name
            </label>
            <div className="relative flex items-center">
              <input
                ref={inputRef}
                readOnly={!isEditing}
                type="text"
                id="deviceName"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                className={`w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none cursor-default ${
                  isEditing &&
                  "focus:ring-2 focus:ring-custom-bluenew cursor-pointer"
                }`}
              />
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="absolute right-3 hover:bg-gray-300 rounded-full p-1"
                >
                  <Pencil size={20} />
                </button>
              ) : (
                <button
                  className="absolute right-3 hover:bg-gray-300 rounded-full p-1"
                  onClick={handleUpdateDeviceName}
                >
                  <Check size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Connection protocol and ip address fields*/}
        <div className="flex items-center">
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              Connection Protocol
            </label>
            <p
              className={`w-full p-3 rounded-[4px] bg-white text-custom-bluenew ${
                !data?.connection_protocol && "h-[49px]"
              }`}
            >
              {data?.connection_protocol}
            </p>
          </div>
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              IP Address
            </label>
            <p
              className={`w-full p-3 rounded-[4px] bg-white text-custom-bluenew ${
                !data?.ip_address && "h-[49px]"
              }`}
            >
              {data?.ip_address}
            </p>
          </div>
        </div>

        {/* Device OS type and CPU type inputs */}
        <div className="flex items-center">
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              Device OS Type
            </label>
            <p
              className={`w-full p-3 rounded-[4px] bg-white text-custom-bluenew ${
                !data?.device_os_type && "h-[49px]"
              }`}
            >
              {data?.device_os_type}
            </p>
          </div>
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              Device CPU Type
            </label>
            <p
              className={`w-full p-3 rounded-[4px] bg-white text-custom-bluenew ${
                !data?.device_cpu_type && "h-[49px]"
              }`}
            >
              {data?.device_cpu_type}
            </p>
          </div>
        </div>

        {/* Health Refresh Time */}
        <div className="flex items-center">
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              Health Refresh Time
            </label>

            <p className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew">
              Health Refresh Time
            </p>
          </div>
        </div>

        {/*Sensor agent version and update button */}
        <div className="flex items-center">
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              Sensor Agent version
            </label>
            <p className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew">
              {sensorAgentVersion}
            </p>
          </div>
          <div className="p-[10px] w-1/2 mt-8 flex gap-4">
            <select
              onChange={updateSensorAgentVersion}
              className="w-1/2 h-[40px] px-4 py-2 rounded-lg bg-custom-blue7 text-white text-center outline-none cursor-pointer appearance-none relative"
            >
              <option
                value=""
                className="bg-white text-gray-800 font-semibold text-sm text-center"
              >
                Update Sensor Agent Version
              </option>
              {sensorAgents.map((agent) => (
                <option
                  // key={agent.id}
                  key={agent.sensor_agent_id}
                  // value={agent.version}
                  value={agent.sensor_agent_version}
                  className="bg-white text-gray-800 xl:text-base lg:text-sm"
                >
                  {/* {agent.name} */}
                  {agent.sensor_agent_name}
                </option>
              ))}
            </select>
            <div className="w-1/2"></div>
          </div>
        </div>

        {/*Last key generated and change key button */}
        <div className="flex items-center">
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              Token Rotated Timestamp
            </label>
            <p className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew">
              12/04/2025
            </p>
          </div>
          <div className="p-[10px] w-1/2 mt-8 flex gap-4">
            <button className="bg-custom-blue7 rounded-lg font-semibold text-sm text-white w-1/2 h-[40px]">
              Force Token Rotate
            </button>
            <div className="w-1/2"></div>
          </div>
        </div>

        {/*Device Status and buttons*/}
        <div className="flex items-center">
          <div className="p-[10px] space-y-[10px] w-1/2">
            <label className="font-bold text-sm text-custom-bluenew">
              Device Status
            </label>
            <p className="w-full p-3 rounded-[4px] bg-white text-custom-bluenew focus:outline-none focus:ring-2 focus:ring-custom-bluenew">
              Pause, 12/04/2025
            </p>
          </div>
          <div className="p-[10px] w-1/2 mt-8 flex gap-4">
            <button className="bg-custom-blue7 rounded-lg font-semibold text-sm text-white w-1/2 h-[40px]">
              Resume
            </button>
            <button className="bg-custom-blue7 rounded-lg font-semibold text-sm text-white w-1/2 h-[40px]">
              Deactivate
            </button>
          </div>
        </div>
      </div>
      <div className="space-x-16 mb-4 mt-8 text-center">
        <button className="h-[40px] bg-custom-bluenew text-white font-semibold text-sm px-6 rounded-lg">
          Force Scan
        </button>
        <button
          type="button"
          onClick={() => dispatch({ type: "CLOSE" })}
          className="h-[40px] bg-custom-primaryBlue text-white font-semibold text-sm px-6 rounded-lg"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ViewUpdate;
